import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Package, Search, Filter, ArrowLeft, Eye, CheckCircle, XCircle,
  Star, TrendingUp, Trash2, Archive, BarChart3, MapPin, DollarSign,
  Calendar, Grid3x3, List, ChevronLeft, ChevronRight, Loader2, AlertCircle,
  Zap, Image, CalendarRange
} from 'lucide-react';
import { categories } from '../mock';
import axios from 'axios';
import { useCountry } from '../context/CountryContext';

const AdManagement = () => {
  const { formatCurrency } = useCountry();
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    search: '',
    sortBy: 'recent',
    dateFrom: '',
    dateTo: ''
  });
  
  // Modals
  const [selectedAd, setSelectedAd] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showFeaturedModal, setShowFeaturedModal] = useState(false);
  
  // Selection for bulk actions
  const [selectedAds, setSelectedAds] = useState([]);
  
  // Rejection form
  const [rejectionData, setRejectionData] = useState({
    reason: '',
    category: 'policy_violation'
  });
  
  // Featured form
  const [featuredData, setFeaturedData] = useState({
    duration: 30,
    position: 1
  });

  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    // Guard: only admins may view this page
    const adminToken = localStorage.getItem('admin_token');
    if (!adminToken) {
      navigate('/admin/login', { replace: true });
      return;
    }
    setAuthChecked(true);
  }, [navigate]);

  useEffect(() => {
    if (!authChecked) return;
    fetchAds();
    fetchAnalytics();
  }, [currentPage, filters, authChecked]);

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchAds = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeader();
      
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        view_mode: viewMode,
        sort_by: filters.sortBy
      });
      
      if (filters.status) params.append('status', filters.status);
      if (filters.category) params.append('category', filters.category);
      if (filters.search) params.append('search', filters.search);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/ads/?${params.toString()}`,
        { headers }
      );
      
      setAds(response.data.ads);
      setStats(response.data.stats);
      setTotalPages(response.data.pages);
    } catch (err) {
      console.error('Error fetching ads:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const headers = getAuthHeader();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/ads/analytics/overview?days=30`,
        { headers }
      );
      setAnalytics(response.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
    }
  };

  const handleUpdateStatus = async (adId, newStatus) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/ads/${adId}/status`,
        { status: newStatus },
        { headers: getAuthHeader() }
      );
      alert(`Ad ${newStatus} successfully`);
      fetchAds();
    } catch (err) {
      alert('Failed to update status');
    }
  };

  const handleBulkApproval = async (action) => {
    if (selectedAds.length === 0) {
      alert('Please select ads first');
      return;
    }
    
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/ads/bulk-approval`,
        { ad_ids: selectedAds, action },
        { headers: getAuthHeader() }
      );
      alert(`${selectedAds.length} ads ${action}ed successfully`);
      setSelectedAds([]);
      fetchAds();
    } catch (err) {
      alert(`Failed to ${action} ads`);
    }
  };

  const handleReject = async () => {
    if (!rejectionData.reason.trim()) {
      alert('Please provide a reason');
      return;
    }
    
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/ads/${selectedAd.id}/reject`,
        {
          reason: rejectionData.reason,
          reason_category: rejectionData.category,
          notify_user: true
        },
        { headers: getAuthHeader() }
      );
      alert('Ad rejected successfully');
      setShowRejectModal(false);
      setRejectionData({ reason: '', category: 'policy_violation' });
      fetchAds();
    } catch (err) {
      alert('Failed to reject ad');
    }
  };

  const handleSetFeatured = async (isFeatured) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/ads/${selectedAd.id}/featured`,
        {
          is_featured: isFeatured,
          featured_duration: featuredData.duration,
          featured_position: featuredData.position
        },
        { headers: getAuthHeader() }
      );
      alert(`Ad ${isFeatured ? 'featured' : 'unfeatured'} successfully`);
      setShowFeaturedModal(false);
      fetchAds();
    } catch (err) {
      alert('Failed to update featured status');
    }
  };

  const handleDeleteAd = async (adId, permanent = false) => {
    if (!window.confirm(`Are you sure you want to ${permanent ? 'permanently delete' : 'archive'} this ad?`)) {
      return;
    }
    
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/ads/${adId}?permanent=${permanent}`,
        { headers: getAuthHeader() }
      );
      alert(`Ad ${permanent ? 'deleted' : 'archived'} successfully`);
      fetchAds();
    } catch (err) {
      alert('Failed to delete ad');
    }
  };

  const toggleAdSelection = (adId) => {
    setSelectedAds(prev =>
      prev.includes(adId) ? prev.filter(id => id !== adId) : [...prev, adId]
    );
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-500',
      pending: 'bg-yellow-500',
      rejected: 'bg-red-500',
      expired: 'bg-gray-500',
      sold: 'bg-blue-500',
      archived: 'bg-gray-400'
    };
    return <Badge className={colors[status]}>{status?.toUpperCase()}</Badge>;
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-12 w-12 animate-spin text-purple-600" />
      </div>
    );
  }

  // Block render until admin auth verified - prevents flicker + satisfies E2E auth-guard tests
  if (!authChecked) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Link to="/admin/dashboard">
                <Button variant="outline" size="sm" className="bg-white/10 hover:bg-white/20 border-white/30 text-white">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Package className="h-8 w-8" />
              <div>
                <h1 className="text-2xl font-bold">Ad Management</h1>
                <p className="text-blue-100 text-sm">Moderate & manage all listings</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Link to="/admin/ads/auto-approval">
                <Button variant="outline" size="sm" className="bg-white/10 hover:bg-white/20 border-white/30 text-white">
                  <Zap className="h-4 w-4 mr-2" />
                  Auto-Approval
                </Button>
              </Link>
              <Link to="/admin/ads/banners">
                <Button variant="outline" size="sm" className="bg-white/10 hover:bg-white/20 border-white/30 text-white">
                  <Image className="h-4 w-4 mr-2" />
                  Banners
                </Button>
              </Link>
              <Link to="/admin/ads/analytics">
                <Button variant="outline" size="sm" className="bg-white/10 hover:bg-white/20 border-white/30 text-white">
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Analytics
                </Button>
              </Link>
              <Button
                variant="outline"
                size="sm"
                className={`bg-white/10 hover:bg-white/20 border-white/30 text-white ${viewMode === 'list' ? 'bg-white/30' : ''}`}
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className={`bg-white/10 hover:bg-white/20 border-white/30 text-white ${viewMode === 'grid' ? 'bg-white/30' : ''}`}
                onClick={() => setViewMode('grid')}
              >
                <Grid3x3 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
            <Card className="bg-gradient-to-br from-gray-700 to-gray-800 text-white">
              <CardContent className="p-4">
                <p className="text-xs opacity-80">Total</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
              <CardContent className="p-4">
                <p className="text-xs opacity-80">Active</p>
                <p className="text-2xl font-bold">{stats.active}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white">
              <CardContent className="p-4">
                <p className="text-xs opacity-80">Pending</p>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
              <CardContent className="p-4">
                <p className="text-xs opacity-80">Rejected</p>
                <p className="text-2xl font-bold">{stats.rejected}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CardContent className="p-4">
                <p className="text-xs opacity-80">Sold</p>
                <p className="text-2xl font-bold">{stats.sold}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-gray-400 to-gray-500 text-white">
              <CardContent className="p-4">
                <p className="text-xs opacity-80">Expired</p>
                <p className="text-2xl font-bold">{stats.expired}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <div>
                <Label>Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search ads..."
                    className="pl-10"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={filters.status || 'all'} onValueChange={(value) => setFilters({ ...filters, status: value === 'all' ? '' : value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={filters.category || 'all'} onValueChange={(value) => setFilters({ ...filters, category: value === 'all' ? '' : value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.slug}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>From Date</Label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                />
              </div>
              <div>
                <Label>To Date</Label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                />
              </div>
              <div>
                <Label>Sort By</Label>
                <Select value={filters.sortBy} onValueChange={(value) => setFilters({ ...filters, sortBy: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recent">Most Recent</SelectItem>
                    <SelectItem value="views">Most Viewed</SelectItem>
                    <SelectItem value="price_high">Price: High to Low</SelectItem>
                    <SelectItem value="price_low">Price: Low to High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Bulk Actions */}
            {selectedAds.length > 0 && (
              <div className="mt-4 flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <span className="text-sm font-medium">{selectedAds.length} ads selected</span>
                <Button size="sm" variant="outline" onClick={() => handleBulkApproval('approve')}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve All
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleBulkApproval('reject')}>
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject All
                </Button>
                <Button size="sm" variant="outline" onClick={() => setSelectedAds([])}>
                  Clear Selection
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="listings" className="space-y-6">
          <TabsList>
            <TabsTrigger value="listings">Listings</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="moderation">Moderation Tools</TabsTrigger>
          </TabsList>

          {/* Listings Tab */}
          <TabsContent value="listings">
            <Card>
              <CardContent className="p-6">
                {loading ? (
                  <div className="flex justify-center py-20">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  </div>
                ) : ads.length === 0 ? (
                  <div className="text-center py-20">
                    <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No ads found</p>
                  </div>
                ) : viewMode === 'list' ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="p-3 text-left">
                            <Checkbox
                              checked={selectedAds.length === ads.length}
                              onCheckedChange={(checked) => {
                                setSelectedAds(checked ? ads.map(a => a.id) : []);
                              }}
                            />
                          </th>
                          <th className="p-3 text-left">Title</th>
                          <th className="p-3 text-left">Seller</th>
                          <th className="p-3 text-left">Category</th>
                          <th className="p-3 text-left">Price</th>
                          <th className="p-3 text-left">Status</th>
                          <th className="p-3 text-left">Views</th>
                          <th className="p-3 text-left">Date</th>
                          <th className="p-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ads.map(ad => (
                          <tr key={ad.id} className="border-b hover:bg-gray-50">
                            <td className="p-3">
                              <Checkbox
                                checked={selectedAds.includes(ad.id)}
                                onCheckedChange={() => toggleAdSelection(ad.id)}
                              />
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                {ad.featured && <Star className="h-4 w-4 text-yellow-500" />}
                                {ad.promoted && <TrendingUp className="h-4 w-4 text-green-500" />}
                                <span className="font-medium">{ad.title}</span>
                              </div>
                            </td>
                            <td className="p-3">{ad.seller?.name}</td>
                            <td className="p-3 capitalize">{ad.category}</td>
                            <td className="p-3">{formatCurrency(parseInt(ad.price))}</td>
                            <td className="p-3">{getStatusBadge(ad.status)}</td>
                            <td className="p-3">
                              <div className="flex items-center gap-1">
                                <Eye className="h-4 w-4 text-gray-400" />
                                <span>{ad.views || 0}</span>
                              </div>
                            </td>
                            <td className="p-3 text-sm text-gray-600">
                              {new Date(ad.created_at).toLocaleDateString()}
                            </td>
                            <td className="p-3">
                              <div className="flex gap-2 justify-end">
                                {ad.status === 'pending' && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleUpdateStatus(ad.id, 'active')}
                                    >
                                      <CheckCircle className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setSelectedAd(ad);
                                        setShowRejectModal(true);
                                      }}
                                    >
                                      <XCircle className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedAd(ad);
                                    setShowFeaturedModal(true);
                                  }}
                                >
                                  <Star className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleDeleteAd(ad.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {ads.map(ad => (
                      <Card key={ad.id} className="overflow-hidden">
                        <div className="relative">
                          <img
                            src={ad.images?.[0] ? `${process.env.REACT_APP_BACKEND_URL}${ad.images[0]}` : 'https://via.placeholder.com/300x200'}
                            alt={ad.title}
                            className="w-full h-48 object-cover"
                          />
                          <div className="absolute top-2 right-2">
                            {getStatusBadge(ad.status)}
                          </div>
                        </div>
                        <CardContent className="p-4">
                          <h3 className="font-semibold truncate">{ad.title}</h3>
                          <p className="text-lg font-bold text-blue-600">{formatCurrency(parseInt(ad.price))}</p>
                          <div className="flex items-center gap-4 text-sm text-gray-600 mt-2">
                            <span className="flex items-center gap-1">
                              <Eye className="h-4 w-4" />
                              {ad.views || 0}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {new Date(ad.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex gap-2 mt-4">
                            {ad.status === 'pending' && (
                              <>
                                <Button size="sm" className="flex-1" onClick={() => handleUpdateStatus(ad.id, 'active')}>
                                  Approve
                                </Button>
                                <Button size="sm" variant="outline" className="flex-1" onClick={() => {
                                  setSelectedAd(ad);
                                  setShowRejectModal(true);
                                }}>
                                  Reject
                                </Button>
                              </>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                <div className="flex items-center justify-between mt-6">
                  <p className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Category Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <BarChart3 className="h-5 w-5 mr-2" />
                    Ads by Category
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics?.by_category?.length > 0 ? (
                    <div className="space-y-3">
                      {analytics.by_category.slice(0, 10).map((item, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <span className="capitalize">{item.category}</span>
                          <Badge>{item.count}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No data available</p>
                  )}
                </CardContent>
              </Card>

              {/* Top Performing */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <TrendingUp className="h-5 w-5 mr-2" />
                    Top Performing Ads
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics?.top_performing?.length > 0 ? (
                    <div className="space-y-3">
                      {analytics.top_performing.slice(0, 5).map((ad, index) => (
                        <div key={index} className="flex items-center justify-between pb-2 border-b last:border-0">
                          <div className="flex-1">
                            <p className="font-medium truncate">{ad.title}</p>
                            <p className="text-xs text-gray-500">{formatCurrency(parseInt(ad.price))}</p>
                          </div>
                          <Badge variant="outline">{ad.views} views</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No data available</p>
                  )}
                </CardContent>
              </Card>

              {/* Price Analysis */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <DollarSign className="h-5 w-5 mr-2" />
                    Price Analysis by Category
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics?.price_analysis?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="p-2 text-left">Category</th>
                            <th className="p-2 text-right">Avg Price</th>
                            <th className="p-2 text-right">Min Price</th>
                            <th className="p-2 text-right">Max Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analytics.price_analysis.map((item, index) => (
                            <tr key={index} className="border-b">
                              <td className="p-2 capitalize">{item.category}</td>
                              <td className="p-2 text-right">{formatCurrency(parseInt(item.avg_price))}</td>
                              <td className="p-2 text-right">{formatCurrency(parseInt(item.min_price))}</td>
                              <td className="p-2 text-right">{formatCurrency(parseInt(item.max_price))}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No data available</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Moderation Tools Tab */}
          <TabsContent value="moderation">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  Moderation Tools
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold mb-2">Quick Actions</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Button variant="outline" className="justify-start">
                        <AlertCircle className="h-4 w-4 mr-2" />
                        Detect Duplicates
                      </Button>
                      <Button variant="outline" className="justify-start">
                        <XCircle className="h-4 w-4 mr-2" />
                        Review Flagged Ads
                      </Button>
                      <Button variant="outline" className="justify-start">
                        <Archive className="h-4 w-4 mr-2" />
                        Archive Expired Ads
                      </Button>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-2">Rejection Reasons</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <Badge variant="outline">Inappropriate Content</Badge>
                      <Badge variant="outline">Duplicate Listing</Badge>
                      <Badge variant="outline">Fake Product</Badge>
                      <Badge variant="outline">Policy Violation</Badge>
                      <Badge variant="outline">Incorrect Pricing</Badge>
                      <Badge variant="outline">Spam</Badge>
                      <Badge variant="outline">Misleading Info</Badge>
                      <Badge variant="outline">Other</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Ad</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Rejection Category</Label>
              <Select value={rejectionData.category} onValueChange={(value) => setRejectionData({ ...rejectionData, category: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inappropriate">Inappropriate Content</SelectItem>
                  <SelectItem value="duplicate">Duplicate Listing</SelectItem>
                  <SelectItem value="fake">Fake Product</SelectItem>
                  <SelectItem value="policy_violation">Policy Violation</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason *</Label>
              <Textarea
                placeholder="Explain why this ad is being rejected..."
                value={rejectionData.reason}
                onChange={(e) => setRejectionData({ ...rejectionData, reason: e.target.value })}
                rows={4}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowRejectModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleReject} className="flex-1">
                Reject Ad
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Featured Modal */}
      <Dialog open={showFeaturedModal} onOpenChange={setShowFeaturedModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Featured Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Featured Duration (days)</Label>
              <Input
                type="number"
                value={featuredData.duration}
                onChange={(e) => setFeaturedData({ ...featuredData, duration: parseInt(e.target.value) })}
                min="1"
              />
            </div>
            <div>
              <Label>Featured Position</Label>
              <Input
                type="number"
                value={featuredData.position}
                onChange={(e) => setFeaturedData({ ...featuredData, position: parseInt(e.target.value) })}
                min="1"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => handleSetFeatured(false)} className="flex-1">
                Remove Featured
              </Button>
              <Button onClick={() => handleSetFeatured(true)} className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500">
                Set Featured
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdManagement;
